<template>
  <div>
    <v-btn
      @click="dialog = true"
      depressed
      color="primary"
      class="button-base"
      :disabled="disabled"
    >
      Добавить документ
    </v-btn>

    <v-dialog v-model="dialog" persistent max-width="600px">
      <form @submit.prevent="upload" class="form form-base">
        <div class="form-base__title mb-4">Добавление документа</div>

        <v-row>
          <template v-if="hasDescription">
            <v-col cols="12">
              <v-select
                label="Выберите тип документа"
                v-if="withSelect"
                :items="documentTypesItems"
                item-text="name"
                item-value="value"
                outlined
                v-model.trim="$v.description.$model"
                :hide-details="descriptionError"
                :error-messages="getValidationErrorText($v.description)"
                @change="validateDescription()"
								@blur="$v.description.$touch()"
              >
              </v-select>
              <v-text-field
                v-else
                v-model.trim="$v.description.$model"
                :error-messages="getValidationErrorText($v.description)"
                label="Наименование и реквизиты документа"
                outlined
								@blur="$v.description.$touch()"
              />
              <div class="ud-error" v-if="descriptionError && withSelect">
                <div class="ud-error__wrapper">
                  <div
                    class="ud-error__text"
                  >
                    {{ descriptionError }}
                  </div>
                </div>
              </div>
              <div class="ud-error" v-if="$v.description.$invalid && withSelect">
                <div class="ud-error__wrapper">
                  <div
                    class="ud-error__text"
                    v-for="(error, key) in getValidationErrorText($v.description)"
                    :key="key"
                  >
                    {{ error }}
                  </div>
                </div>
              </div>
            </v-col>
            <v-col cols="12" v-if="singleZip" class="mt-0 mb-0 pt-0 pb-0">
              <div class="alert-base">
                <v-alert
                  icon="mdi-exclamation-thick"
                  prominent
                  text
                  dense
                  type="info"
                  color="primary"
                >
                  Передаваемый пакет документов перед загрузкой должен быть
                  упакован в zip-архив.
                  <span v-if="zipSizeMax25">
                    Подпись сертификатом в браузере доступна только для файлов
                    меньше 25 мб.
                  </span>
                </v-alert>
              </div>
            </v-col>
          </template>

          <v-col cols="12">
            <v-file-input
              v-model="$v.attachedFile.$model"
              @click:clear="reset(InputType.FILE)"
              :accept="acceptFile"
              :error-messages="getValidationErrorText($v.attachedFile)"
              label="Файл"
              outlined
              :hint="`${hint ? $t(`${translationKey}.hint`) : ''}`"
              persistent-hint
							@blur="$v.attachedFile.$touch()"
            />
          </v-col>
          <v-col>
            <v-file-input
              v-model="$v.attachedFileSig.$model"
              @click:clear="reset(InputType.FILE_SIG)"
              :disabled="!Boolean(attachedFile)"
              :accept="acceptSign"
              :error-messages="getValidationErrorText($v.attachedFileSig)"
              label="ЭП файла"
              outlined
							@blur="$v.attachedFileSig.$touch()"
            />
          </v-col>
          <v-col cols="auto">
            <v-btn
              :disabled="btnSignDisabled"
              depressed
              class="button-base-white"
              @click="signFile"
              :loading="loadingSign"
            >
              Подписать
            </v-btn>
          </v-col>
        </v-row>

        <div v-if="singleZip && loading">
          <div class="loading-text">
            Файл загружается. Продолжительность процесса загрузки зависит от
            размера файла.
          </div>
        </div>

        <div v-if="error" class="error--text">
          {{ error }}
        </div>

        <v-row justify="end">
          <v-col cols="auto">
            <v-btn @click="dialog = false" depressed class="button-base-white">
              Отмена
            </v-btn>
          </v-col>

          <v-col cols="auto">
            <v-btn
              type="submit"
              depressed
              color="primary"
              class="button-base"
              :loading="loading"
            >
              Добавить файл в опись
            </v-btn>
          </v-col>
        </v-row>
      </form>
    </v-dialog>
  </div>
</template>

<script>
import {mdiTrashCanOutline} from '@mdi/js';
import {maxLength, required, requiredIf} from 'vuelidate/lib/validators';

import {
  EMOJI,
  FILE_NAME_MAX_LENGTH,
  FILE_SIG_NAME,
  FILE_SIZE,
  FILE_TYPE,
  getValidationErrorText,
} from '@/utils/validate';
import {saveFile, saveSigFile} from '@/api/filestorage';
import {ErrorAlertModule} from '@/store/modules/errors';
import {ServicesModule} from '@/store/modules/services';
import {errorHandler} from '@/utils/common';
import {checkSign} from '@/api/checkSign';
import {UploadDocumentMainGroups} from "@/utils/uploadDocumentTypes";

const InputType = {
  FILE: 'file',
  FILE_SIG: 'fileSig',
};

export default {
  name: 'UploadDocument',
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    acceptFile: {
      type: Array,
      default: () => ['.pdf'],
    },
    acceptSign: {
      type: Array,
      default: () => ['.sig'],
    },
    hasDescription: {
      type: Boolean,
      default: false,
    },
    maxLength: {
      type: Number,
      default: 1e6,
    },
    singleZip: {
      type: Boolean,
      default: false,
    },
    maxFileSize: {
      type: Number,
      default: 25,
    },
    disabledSign: {
      type: Boolean,
      default: false,
    },
    disabledSigNameValidation: {
      type: Boolean,
      default: false,
    },
    withSelect: {
      type: Boolean,
      default: false
    },
    documentTypes: {
      type: Array,
      required: false,
    },
		hint: {
			type: Boolean,
			default: true,
		},
    translationKey: {
      type: String,
      default: 'common.uploadDocument'
    }
  },
  data: () => ({
    InputType,
    mdiTrashCanOutline,
    getValidationErrorText,
    dialog: false,
    description: '',
    error: '',
    attachedFile: null,
    attachedFileSig: null,
    isSigningPlugin: false,
    loading: false,
    loadingSign: false,
    descriptionError: ''
  }),
  computed: {
    documentTypesItems() {
      if (!this.documentTypes) return
      let groups = []
      UploadDocumentMainGroups.forEach(group => {
        let items = this.documentTypes
          .filter(type => group.subgroups.includes(type.groupCode))
        if (!items.length) return
        groups = [
          ...groups,
          {
            header: group.groupName
          }
        ]

        items = items.map(item => {
          return item.documents
        }).flat()
        groups = [
          ...groups,
          ...items
        ]
      })

      return groups.map(item => {
        if (item.header) {
          return {
            header: item.header.charAt(0).toUpperCase() + item.header.slice(1),
          }
        }
        return {
          name: item.name.charAt(0).toUpperCase() + item.name.slice(1),
          value: item.value
        }
      })
    },
    zipSizeMax25() {
      return (
        this.singleZip &&
        this.attachedFile &&
        this.attachedFile.size >= 25 * 1000 * 1000
      );
    },

    btnSignDisabled() {
      if (this.disabledSign) return true;

      return (
        Boolean(this.attachedFileSig) || !this.attachedFile || this.zipSizeMax25
      );
    },
  },
  validations() {
    return {
      description: {
        required: requiredIf(() => this.hasDescription),
        maxLength: maxLength(this.maxLength),
        EMOJI,
      },
      attachedFile: {
        required,
        FILE_TYPE: FILE_TYPE(this.acceptFile),
        FILE_SIZE: FILE_SIZE(this.maxFileSize),
        FILE_NAME_MAX_LENGTH: FILE_NAME_MAX_LENGTH(100),
      },
      attachedFileSig: {
        required,
        FILE_TYPE: FILE_TYPE(this.acceptSign),
        FILE_SIZE: FILE_SIZE(this.maxFileSize),
        FILE_NAME_MAX_LENGTH: FILE_NAME_MAX_LENGTH(100),
        FILE_SIG_NAME: FILE_SIG_NAME(
          this.attachedFile,
          this.disabledSigNameValidation
        ),
      },
    };
  },
  methods: {
    reset(inputType) {
      if (inputType === InputType.FILE || !inputType) {
        this.attachedFile = null;
      }

      this.$v.$reset();
      this.attachedFileSig = null;
      this.isSigningPlugin = false;
    },
    validateDescription() {
      const unwantedDocs = ['12303', '12304', '12305', '12306', '12399',]
      this.descriptionError = ''
      if (unwantedDocs.includes(this.description)) {
        this.descriptionError = 'Предоставление данного документа нежелательно'
      }
    },
    async upload() {
      ErrorAlertModule.setErrorAlert('');
      this.error = '';

      this.$v.$touch();

      if (this.$v.$invalid) return;

      this.loading = true;

      try {
        if (this.isSigningPlugin) {
          const file = await saveFile(this.attachedFile);
          const fileSig = await saveSigFile({
            sigBas64: this.attachedFileSig.sigBas64,
            fileName: this.attachedFileSig.fileName,
          });

          this.$emit('change', {
            description: this.description,
            file,
            fileSig,
          });

          this.dialogCloseHandler();

          return;
        }

        const {file, signFile, message, status} = await checkSign(
          this.attachedFile,
          this.attachedFileSig
        );

        if (status === 'OK') {
          this.$emit('change', {
            description: this.description,
            file,
            fileSig: signFile,
          });

          this.dialogCloseHandler();

          return;
        }

        this.error = message;

        this.loading = false;
      } catch (error) {
        errorHandler(error);

        this.dialogCloseHandler();

        await this.$vuetify.goTo('#header-target', {duration: 200});
      }
    },

    dialogCloseHandler() {
      this.loading = false;
      this.dialog = false;
      this.reset();
      this.description = '';
    },

    signFile() {
      // Создать саму подпись
      const file = this.attachedFile;
      let reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = async () => {
        this.loadingSign = true;

        let header = ';base64,';
        let fileData = reader.result;
        let fileContent = fileData.substr(
          fileData.indexOf(header) + header.length
        );
        try {
          const signs = await ServicesModule.runSign({
            sigBas64: fileContent,
          });
          this.isSigningPlugin = true;
          this.attachedFileSig = {
            sigBas64: signs.sigBas64[0],
            fileName: file.name,
            name: `${file.name}.sig`,
          };
        } catch {
          await this.$vuetify.goTo('#header-target', {duration: 200});
        }

        this.loadingSign = false;
      };
    },
  },
};
</script>

<style scoped lang="scss">
.form {
  padding: 24px;
  background-color: #ffffff;
}

::v-deep .v-subheader {
  color: black;
  background: #EBEBEB;
  font-size: 20px;
}

::v-deep .v-list-item__title {
  font-size: 18px;
}
.ud-error {
  padding-top: 8px;
}

.ud-error__text {
  font-size: 12px;
  line-height: 1;
  color: $color-error;
}

.ud-error__wrapper {
  min-height: 12px;
}
</style>
